import { useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/providers/auth-provider'
import { signInSchema, type SignInInput } from '@/lib/validations'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { AuthShell } from './auth-shell'

export function LoginPage() {
  const { session, signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [submitting, setSubmitting] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInInput>({ resolver: zodResolver(signInSchema) })

  if (session) {
    const from = (location.state as { from?: string })?.from ?? '/'
    return <Navigate to={from} replace />
  }

  async function onSubmit(values: SignInInput) {
    setSubmitting(true)
    try {
      await signIn(values.email, values.password)
      toast.success('Bem-vindo de volta')
      navigate('/', { replace: true })
    } catch (err) {
      toast.error('Não foi possível entrar', {
        description: err instanceof Error ? traduz(err.message) : 'Verifique suas credenciais.',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell title="Entrar" subtitle="Acesse com seu e-mail e senha.">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Field label="E-mail" htmlFor="email" error={errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="voce@empresa.com"
            {...register('email')}
          />
        </Field>
        <Field label="Senha" htmlFor="password" error={errors.password?.message}>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            {...register('password')}
          />
        </Field>
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Entrar
        </Button>
      </form>
      <div className="mt-4 text-center text-sm">
        <Link to="/recuperar-senha" className="text-primary hover:underline">
          Esqueci minha senha
        </Link>
      </div>
      <p className="mt-4 text-center text-xs text-muted-foreground">
        Não há cadastro aberto. O acesso é por convite do gestor.
      </p>
    </AuthShell>
  )
}

function traduz(msg: string): string {
  if (/invalid login credentials/i.test(msg)) return 'E-mail ou senha incorretos.'
  if (/email not confirmed/i.test(msg)) return 'E-mail ainda não confirmado.'
  return msg
}
